import React from 'react';
import { createRoot } from 'react-dom/client';
import { parseDataProps } from '@utils/hostProps';
import IslandErrorBoundary from './IslandErrorBoundary';
import IslandRoot from './IslandRoot';
import { IslandHost, addHosts } from './hosts';
import { getIsland } from './registry';
import { IslandName } from './types';

const ISLAND_ATTR = 'data-react-island';
const ROOT_ATTR = 'data-react-island-root';
const ROOT_CLASS = 'island-root';

const mounted = new WeakSet<Element>();
let isRootCreated = false;

// One React root per page, in a detached container: the islands themselves are
// portals into their host elements (see `IslandRoot`).
const ensureRoot = () => {
  if (isRootCreated) {
    return;
  }

  isRootCreated = true;

  const container = document.createElement('div');
  container.setAttribute(ROOT_ATTR, '');
  container.hidden = true;
  document.body.appendChild(container);

  createRoot(container).render(
    React.createElement(
      IslandErrorBoundary,
      null,
      React.createElement(IslandRoot)
    )
  );
};

/** Mounts every island host on the page. Safe to call again — mounts once per element. */
export const mount = () => {
  const newHosts: Omit<IslandHost, 'id'>[] = [];

  document.querySelectorAll<HTMLElement>(`[${ISLAND_ATTR}]`).forEach((el) => {
    if (mounted.has(el)) {
      return;
    }

    const name = el.getAttribute(ISLAND_ATTR);
    const Component = getIsland(name);
    if (!Component) {
      return;
    }

    mounted.add(el);
    el.classList.add(ROOT_CLASS);

    newHosts.push({
      // A registered name: `getIsland` found its component.
      name: name as IslandName,
      el,
      Component,
      props: parseDataProps(el),
      placeholderNodes: Array.from(el.childNodes),
    });
  });

  if (!newHosts.length) {
    return;
  }

  addHosts(newHosts);
  ensureRoot();
};
